import React, { useMemo, useState, useCallback } from "react";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { useFormik } from "formik";
import {
    Col,
    Row,
    Card,
    CardBody,
    Button,
    Modal,
    ModalHeader,
    ModalBody,
    Form,
    Input,
    Label,
    FormFeedback,
} from "reactstrap";

import TableContainer from "components/Common/TableContainer";
import Breadcrumbs from "components/Common/Breadcrumb";
import DeleteModal from "components/Common/DeleteModal";
import GlobalInputSearch from "components/Common/GlobalInputSearch";

import {
    useKeyTypes,
    useCreateKeyType,
    useEditKeyType,
    useDeleteKeyType,
} from "hooks/keyTypeHook";
import { useListParams } from "hooks/useListParams";
import { formatReadableDate } from "utils/formatDate";

const KeyTypeList = () => {
  document.title = "Key Type List"

  const [modal, setModal] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [keyType, setKeyType] = useState(null)
  const [deleteModal, setDeleteModal] = useState(false)

  const { params, setPage, setSearch, resetParams, hasActiveFilters } = useListParams({
    page: 1,
    search: "",
  })

  const queryParams = useMemo(() => ({
    page: params.page,
    per_page: 10,
    ...(params.search && { search: params.search }),
  }), [params.page, params.search])

  const {
    data: keyTypesResponse,
    isLoading,
    refetch: keyTypeRefetch,
    isRefetching: keyTypeRefetchLoading,
  } = useKeyTypes(queryParams)

  const { mutateAsync: createKeyType, isPending: keyTypeCreateLoading } = useCreateKeyType()
  const { mutateAsync: editKeyType, isPending: keyTypeEditLoading } = useEditKeyType()
  const { mutateAsync: deleteKeyType, isPending: keyTypeDeleteLoading } = useDeleteKeyType()

  const toggle = useCallback(() => {
    if (modal) {
      setModal(false)
      setKeyType(null)
      setIsEdit(false)
    } else setModal(true)
  }, [modal])

  const handleAddNewClick = useCallback(() => {
    setKeyType(null)
    setIsEdit(false)
    toggle()
  }, [toggle])

  const handleKeyTypeClick = useCallback((kt) => {
    setKeyType({ id: kt.id, name: kt.name })
    setIsEdit(true)
    toggle()
  }, [toggle])

  const onClickDelete = useCallback((kt) => {
    setKeyType(kt)
    setDeleteModal(true)
  }, [])

  const handleDeleteKeyType = useCallback(async () => {
    if (keyType?.id) {
      try {
        const res = await deleteKeyType(keyType.id)
        toast.success(res?.message ?? "Key Type deleted successfully.")
        setDeleteModal(false)
      } catch (error) {
        toast.error("Failed to delete key type.")
      }
    }
  }, [keyType, deleteKeyType])

  const handleListRefresh = () => keyTypeRefetch()

  const validationSchema = Yup.object({
    name: Yup.string().trim().min(2).required("Please enter key type name"),
  })

  const onSubmit = useCallback(async (values, { resetForm }) => {
    const payload = { name: values.name }
    const action = isEdit ? editKeyType({ id: keyType.id, ...payload }) : createKeyType(payload)

    try {
      const res = await action
      toast.success(res?.message ?? `Key Type ${isEdit ? "updated" : "created"} successfully.`)
      resetForm()
      toggle()
    } catch (error) {
      toast.error("Failed to save key type.")
    }
  }, [isEdit, keyType, editKeyType, createKeyType, toggle])

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: { name: keyType?.name || "" },
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit,
  })

  const columns = useMemo(() => [
    { header: "ID", accessorKey: "id" },
    { header: "Name", accessorKey: "name" },
    {
      header: "Slug",
      accessorKey: "slug",
    },
    {
      header: "Created",
      accessorKey: "created_at",
      cell: ({ row }) => row.original.created_at ? formatReadableDate(row.original.created_at) : "-",
    },
    {
      header: "Action",
      cell: ({ row }) => (
        <ul className="list-unstyled hstack gap-1 mb-0">
          <li>
            <button
              type="button"
              className="btn btn-sm btn-soft-info"
              onClick={() => handleKeyTypeClick(row.original)}
            >
              <i className="mdi mdi-pencil-outline" />
            </button>
          </li>
          <li>
            <button
              type="button"
              className="btn btn-sm btn-soft-danger"
              onClick={() => onClickDelete(row.original)}
            >
              <i className="mdi mdi-delete-outline" />
            </button>
          </li>
        </ul>
      ),
    },
  ], [handleKeyTypeClick, onClickDelete])

  const keyTypesData = keyTypesResponse?.data?.data || []
  const paginationInfo = keyTypesResponse?.data

  return (
    <React.Fragment>
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDeleteKeyType}
        loading={keyTypeDeleteLoading}
        onCloseClick={() => setDeleteModal(false)}
      />
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="Key Type" breadcrumbItem="Key Type List" />
          <Row>
            <Col lg="12">
              <Card>
                <CardBody className="border-bottom d-flex justify-content-between align-items-center">
                  <h5 className="mb-0 card-title">Key Type List</h5>
                  <div>
                    <Button color="primary" onClick={handleAddNewClick}>Add New Key Type</Button>
                    <Button color="light" onClick={handleListRefresh} className="ms-1">
                      <i className={`mdi mdi-refresh ${keyTypeRefetchLoading ? "mdi-spin" : ""}`} />
                    </Button>
                    {hasActiveFilters && (
                      <Button color="danger" onClick={resetParams} className="ms-1">Reset Filters</Button>
                    )}
                  </div>
                </CardBody>
                <CardBody>
                  <Row className="mb-3">
                    <Col sm={4}>
                      <GlobalInputSearch value={params.search} onChange={setSearch} />
                    </Col>
                  </Row>
                  <TableContainer
                    columns={columns}
                    data={keyTypesData}
                    isPagination={true}
                    paginationInfo={paginationInfo}
                    onPageChange={setPage}
                    loading={isLoading || keyTypeRefetchLoading}
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
          <Modal isOpen={modal} toggle={toggle} centered>
            <ModalHeader toggle={toggle}>{isEdit ? "Edit Key Type" : "Add Key Type"}</ModalHeader>
            <ModalBody>
              <Form onSubmit={e => { e.preventDefault(); validation.handleSubmit() }}>
                <div className="mb-3">
                  <Label htmlFor="name">Key Type Name</Label>
                  <Input
                    name="name"
                    id="name"
                    type="text"
                    placeholder="Enter key type name"
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.name || ""}
                    invalid={validation.touched.name && !!validation.errors.name}
                  />
                  {validation.touched.name && validation.errors.name && (
                    <FormFeedback>{validation.errors.name}</FormFeedback>
                  )}
                </div>
                <div className="text-end">
                  <Button color="success" type="submit" disabled={keyTypeEditLoading || keyTypeCreateLoading}>
                    {isEdit ? "Save Changes" : "Add Key Type"}
                  </Button>
                </div>
              </Form>
            </ModalBody>
          </Modal>
        </div>
      </div>
    </React.Fragment>
  )
}

export default KeyTypeList
